/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Fragment, type ReactNode, useCallback, useEffect } from 'react';

import { css, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/custom-theme-button';
import { SplitButton } from '@atlaskit/button/new';
import AddIcon from '@atlaskit/icon/core/add';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import CrossIcon from '@atlaskit/icon/core/cross';
import {
	TopNavButton,
	TopNavIconButton,
} from '@atlaskit/navigation-system/experimental/top-nav-button';
import { SpotlightPulse, SpotlightTarget } from '@atlaskit/onboarding';
import { fg } from '@atlaskit/platform-feature-flags';
import { Hide, Show, UNSAFE_useMediaQuery as useMediaQuery } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { TRIGGER_BUTTON_TEST_ID } from '../../../constants';
import { useMenuControl } from '../../../context';
import { useUniversalCreateAnalytics } from '../../../hooks';
import { withIntlProvider } from '../../../services';
import type {
	MenuItem,
	TriggerPropsWithSplitPrimaryRef,
	UniversalCreateButtonProps,
} from '../../../types';

const triggerButton = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// below 768px https://staging.atlassian.design/components/primitives/responsive/breakpoints/examples#media-queries
		// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles
		'@media not all and (min-width: 48rem)': {
			display: 'block',

			// to match with the icon button spacing
			paddingLeft: token('space.100', '8px'),
			paddingRight: token('space.100', '8px'),
			// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
			'> span > span': {
				// enforce icon has no extra padding
				paddingLeft: 0,
				paddingRight: 0,
			},
		},
	},
});

/**
 * Refactor this whole logic after T-25, we should use the proper Create component instead.
 * https://jplat.atlassian.net/browse/BLU-7401
 */
const nav3ButtonTextStyles = css({
	// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'@media not all and (min-width: 48rem)': {
			// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
			'&:hover': {
				paddingLeft: token('space.100', '8px'),
				paddingRight: token('space.100', '8px'),
			},
			// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
			'span:nth-of-type(1)': {
				marginLeft: 0,
				marginRight: 0,
			},
			// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
			'span:nth-of-type(2)': {
				display: 'none',
			},
		},
	},
});

const messages = defineMessages({
	secondaryButtonTooltipText: {
		id: 'platform.navigation-experiences.universal-create.secondary-tooltip-text',
		defaultMessage: 'More create options',
	},
});

function TooltipWrapper({
	children,
	text,
	isScreenReaderAnnouncementDisabled = true,
}: {
	children: ReactNode;
	isScreenReaderAnnouncementDisabled?: boolean;
	text: ReactNode;
}) {
	return (
		<Tooltip
			content={text}
			isScreenReaderAnnouncementDisabled={isScreenReaderAnnouncementDisabled}
			position="bottom"
			ignoreTooltipPointerEvents
		>
			{children}
		</Tooltip>
	);
}

export type TriggerButtonProps = {
	buttonTheme?: UniversalCreateButtonProps['buttonTheme'];
	children: string;
	enableTriggerPulse?: boolean;
	isEntryPointEnabled?: boolean;
	isNav4Enabled?: boolean;
	isOpen: boolean;
	onClick: () => void;
	onMouseEnter?: UniversalCreateButtonProps['onMouseEnter'];
	onMouseLeave?: UniversalCreateButtonProps['onMouseLeave'];
	primaryActionItem?: MenuItem;
	testId?: string;
	triggerButtonSpotlightTarget?: string;
	triggerProps: TriggerPropsWithSplitPrimaryRef;
};
function TriggerButtonInner({
	triggerProps,
	testId,
	onClick,
	children,
	isOpen,
	triggerButtonSpotlightTarget,
	enableTriggerPulse,
	isNav4Enabled = true,
	buttonTheme,
	onMouseEnter,
	onMouseLeave,
	primaryActionItem,
	isEntryPointEnabled = false,
}: TriggerButtonProps) {
	const { product, sections } = useMenuControl();
	const { fireCreateButtonClicked, fireCreateButtonViewed } = useUniversalCreateAnalytics();
	const { formatMessage } = useIntl();
	const isDesktop = useMediaQuery('above.sm')?.matches ?? false;
	const version = sections ? 'v2' : 'v1';

	useEffect(() => {
		if (!fg('navx-3261_view-button-event')) {
			return;
		}

		fireCreateButtonViewed({ version });
	}, [fireCreateButtonViewed, version]);

	// Extract splitPrimaryRef from triggerProps for split button variant
	const { splitButtonPrimaryRef, ...restTriggerProps } = triggerProps;

	const onButtonClick = useCallback(() => {
		/// For entrypoint path (isEntryPointEnabled is true), set to expanded when isOpen is true
		// For non-entrypoint path (isEntryPointEnabled is false), set to expanded when isOpen is false (current state), as in the legacy mode, the event is fired before menu opens.
		const itemState = isEntryPointEnabled === isOpen ? 'expanded' : 'collapsed';
		fireCreateButtonClicked({ itemState });
		onClick();
	}, [fireCreateButtonClicked, isOpen, onClick, isEntryPointEnabled]);

	const CreateIcon = isOpen ? CrossIcon : AddIcon;

	const enterEvent = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			onMouseEnter?.(event);
		},
		[onMouseEnter],
	);

	const leaveEvent = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			onMouseLeave?.(event);
		},
		[onMouseLeave],
	);

	const button = (
		<div css={triggerButton}>
			{product === 'confluence' && !isNav4Enabled ? (
				<div css={nav3ButtonTextStyles}>
					<Button
						{...restTriggerProps}
						appearance="primary"
						theme={buttonTheme}
						onMouseEnter={enterEvent}
						onMouseLeave={leaveEvent}
						onClick={onButtonClick}
						iconBefore={<CreateIcon label="" data-vc="global-content-create-button" />}
						testId={testId || TRIGGER_BUTTON_TEST_ID}
						aria-label={children}
						isSelected={isOpen}
					>
						<Show above="sm" as="span">
							{children}
						</Show>
					</Button>
				</div>
			) : (
				<TooltipWrapper text={children}>
					<TopNavButton
						{...restTriggerProps}
						appearance="primary"
						onClick={onButtonClick}
						onMouseEnter={enterEvent}
						onMouseLeave={leaveEvent}
						isSelected={isOpen}
						iconBefore={CreateIcon}
						testId={testId || TRIGGER_BUTTON_TEST_ID}
						aria-label={children}
					>
						<Show above="sm" as="span">
							{children}
						</Show>
					</TopNavButton>
				</TooltipWrapper>
			)}
		</div>
	);

	const responsiveSafeButton = (
		<Fragment>
			<Show above="sm">
				<TooltipWrapper text={children}>
					<TopNavButton
						{...(isDesktop ? restTriggerProps : {})}
						appearance="primary"
						onClick={onButtonClick}
						onMouseEnter={enterEvent}
						onMouseLeave={leaveEvent}
						isSelected={isOpen}
						iconBefore={CreateIcon}
						testId={testId || TRIGGER_BUTTON_TEST_ID}
						aria-label={children}
						data-responsive-variant="desktop"
					>
						{children}
					</TopNavButton>
				</TooltipWrapper>
			</Show>
			<Hide above="sm">
				<TooltipWrapper text={children}>
					<TopNavIconButton
						{...(!isDesktop ? restTriggerProps : {})}
						appearance="primary"
						onClick={onButtonClick}
						onMouseEnter={enterEvent}
						onMouseLeave={leaveEvent}
						isSelected={isOpen}
						icon={CreateIcon}
						testId={`${testId || TRIGGER_BUTTON_TEST_ID}-mobile`}
						label={children}
						data-responsive-variant="mobile"
					/>
				</TooltipWrapper>
			</Hide>
		</Fragment>
	);

	const splitButtonVariant = (
		<Fragment>
			<Show above="sm">
				<SplitButton appearance="primary">
					<TooltipWrapper text={children}>
						<TopNavButton
							ref={splitButtonPrimaryRef}
							appearance="primary"
							onClick={primaryActionItem?.onClick}
							aria-label={children}
							testId="uc-trigger-button-primary"
						>
							{children}
						</TopNavButton>
					</TooltipWrapper>
					<TooltipWrapper
						text={formatMessage(messages.secondaryButtonTooltipText)}
						isScreenReaderAnnouncementDisabled={false} // Not disabling as is different from the children content
					>
						<TopNavIconButton
							{...(isDesktop ? restTriggerProps : {})}
							appearance="primary"
							onClick={onButtonClick}
							isSelected={isOpen}
							icon={ChevronDownIcon}
							data-responsive-variant="desktop"
							label={children}
							testId={`${testId || TRIGGER_BUTTON_TEST_ID}-split`}
							onMouseEnter={onMouseEnter}
							onMouseLeave={onMouseLeave}
						/>
					</TooltipWrapper>
				</SplitButton>
			</Show>
			<Hide above="sm">
				<TooltipWrapper text={children}>
					<TopNavIconButton
						{...(!isDesktop ? restTriggerProps : {})}
						appearance="primary"
						onClick={onButtonClick}
						onMouseEnter={enterEvent}
						onMouseLeave={leaveEvent}
						isSelected={isOpen}
						icon={CreateIcon}
						testId={`${testId || TRIGGER_BUTTON_TEST_ID}-mobile`}
						label={children}
						data-responsive-variant="mobile"
					/>
				</TooltipWrapper>
			</Hide>
		</Fragment>
	);

	const buttonToRender = primaryActionItem
		? splitButtonVariant
		: isNav4Enabled
		? responsiveSafeButton
		: button;

	return triggerButtonSpotlightTarget ? (
		<SpotlightTarget name={triggerButtonSpotlightTarget}>
			<SpotlightPulse radius={3} pulse={enableTriggerPulse}>
				{buttonToRender}
			</SpotlightPulse>
		</SpotlightTarget>
	) : (
		buttonToRender
	);
}

export const TriggerButton = withIntlProvider(TriggerButtonInner);
